import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Input } from "@app/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { Badge } from "@app/components/ui/badge";
import { Checkbox } from "@app/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@app/components/ui/dropdown-menu";
import ScopedPathwayBoard from "@app/components/dashboard-pathway/ScopedPathwayBoard";
import {
  DeleteFolderDialog,
  FolderNameDialog,
  MoveToFolderDialog,
} from "@app/components/FolderDialogs";
import {
  FolderActionsMenu,
  FolderDropBar,
  FolderTile,
} from "@app/components/FolderShelf";
import {
  Search,
  Download,
  Eye,
  Calendar,
  BarChart3,
  Loader2,
  X,
  Users,
  Trash2,
  GitCompareArrows,
  RefreshCw,
  Sigma,
  ArrowLeft,
  FolderClosed,
  FolderInput,
  FolderMinus,
  FolderPlus,
  Inbox,
  MoreVertical,
} from "lucide-react";
import { MAX_STUDY_COHORTS, studyLink } from "@app/components/studies/state";
import API from "@app/services/api";
import { viewAsHeaders } from "@app/services/view-as";
import {
  UNFILED,
  createCohortFolder,
  deleteCohortFolder,
  fetchCohortFolders,
  fileCohort,
  renameCohortFolder,
  type CohortFolder,
} from "@app/utils/cohort-folders";
import {
  refusedAllowance,
  useExportShareCap,
} from "@app/utils/use-export-share-cap";
import { useFolderDrop } from "@app/utils/use-folder-drop";
import { useLabReadOnly } from "@app/utils/use-lab-read-only";
import { usePreviewView } from "@app/zustand/preview-view";
import useUser from "@app/zustand/user";
import { Plural, Trans, useLingui } from "@lingui/macro";
import { toast } from "sonner";

interface Cohort {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  owner_id: string;
  /** The reader's classeur it is filed in (PT-162); null for « Sans classeur ». */
  folder_id?: string | null;
  metadata?: {
    type?: string;
    /** A colleague's cohort under the anonymity threshold has its size withheld (`null`). */
    patient_count?: number | null;
    episode_count?: number | null;
    specialite?: string;
    scope?: string;
    snapshot_at?: string;
  } | null;
}

type FilterType = "all" | "recent" | "largest";

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function byName(a: CohortFolder, b: CohortFolder): number {
  return a.name.localeCompare(b.name, "fr", { sensitivity: "base" });
}

export default function CataloguePage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const currentUser = useUser((state) => state.user);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [catalogFilter, setCatalogFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");

  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const shareCap = useExportShareCap();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [consultCohort, setConsultCohort] = useState<Cohort | null>(null);
  // Compare mode: when active, cohort cards expose checkboxes and the CTA at the bottom of the page
  // opens a study of the picked cohorts on its table. « Comparer » is that study, not a page of its
  // own: two screens drawing one table would be read as two results.
  const [compareMode, setCompareMode] = useState(false);
  const [selectedCompareIds, setSelectedCompareIds] = useState<string[]>([]);
  const { previewRole, previewMedecin, previewSpecialite } = usePreviewView();

  // The reader's classeurs (PT-162). The open one lives in the URL, so the back button and a shared
  // link land on it. Under « Vue médecin » they are the previewed doctor's, read only.
  const [searchParams, setSearchParams] = useSearchParams();
  const openFolderId = searchParams.get("classeur");
  const showFolder = (folderId: string | null) =>
    setSearchParams(folderId ? { classeur: folderId } : {});
  const [folders, setFolders] = useState<CohortFolder[] | null>(null);
  const [naming, setNaming] = useState<{ folder: CohortFolder | null } | null>(
    null,
  );
  const [deletingFolder, setDeletingFolder] = useState<CohortFolder | null>(
    null,
  );
  const [moving, setMoving] = useState<Cohort | null>(null);
  const readOnly = useLabReadOnly();
  const canFile =
    !readOnly && !(previewRole === "medecin" && previewMedecin?.id);

  // The catalogue is cohort-only. Lab datasets are a different concept
  // (research artefacts with files, ontology work, evaluation) and live on
  // /laboratoire and /datasets. Backend already enforces the separation:
  // /catalog/cohortes filters metadata.type == "cohorte"; /dataset/ filters it out.
  const loadCohorts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get({ path: "/catalog/cohortes" });
      if (res.ok) {
        setCohorts(res.data.datasets || []);
      } else {
        setError(res.error || t`Erreur lors du chargement du catalogue`);
      }
    } catch {
      setError(t`Erreur lors du chargement du catalogue`);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const reloadFolders = useCallback(() => {
    fetchCohortFolders()
      .then((next) => setFolders(next))
      // Without its classeurs the catalogue still lists every cohort, unfiled.
      .catch(() => setFolders([]));
  }, []);

  useEffect(() => {
    void loadCohorts();
    reloadFolders();
  }, [
    loadCohorts,
    reloadFolders,
    previewRole,
    previewMedecin?.id,
    previewSpecialite,
  ]);

  const handleDeleteCohort = async (cohort: Cohort) => {
    if (!confirm(t`Supprimer la cohorte « ${cohort.name} » ? Cette action est irréversible.`)) return;
    setDeletingId(cohort.id);
    try {
      const res = await API.delete({ path: `/catalog/cohorte/${cohort.id}` });
      if (res?.ok) {
        void loadCohorts();
      } else {
        alert(res?.error || t`Erreur lors de la suppression de la cohorte`);
      }
    } catch {
      alert(t`Erreur lors de la suppression de la cohorte`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleRefreshCohort = async (cohort: Cohort) => {
    setRefreshingId(cohort.id);
    try {
      const res = await API.post({ path: `/catalog/cohorte/${cohort.id}/refresh` });
      if (res?.ok) {
        void loadCohorts();
      } else {
        alert(res?.error || t`Erreur lors du rafraîchissement de la cohorte`);
      }
    } catch {
      alert(t`Erreur lors du rafraîchissement de la cohorte`);
    } finally {
      setRefreshingId(null);
    }
  };

  /** The cohort as the board's export writes it: its episodes, or its counts when read in aggregate. */
  const handleDownload = async (cohort: Cohort) => {
    setDownloadingId(cohort.id);
    try {
      const query = { cohortId: cohort.id };
      // Patients outside the reader's patientèle are capped (PT-120): asked first, refused by the server.
      if (!(await shareCap.confirm(query))) return;
      const response = await fetch(
        API.getUrl("/dashboard/pathway/export", query),
        {
          method: "GET",
          credentials: "include",
          headers: { Accept: "text/csv", ...viewAsHeaders() },
        },
      );
      if (!response.ok) {
        const refused = await refusedAllowance(response);
        if (refused) {
          shareCap.showRefusal(refused);
        } else {
          alert(t`Impossible de charger les données pour l'export.`);
        }
        return;
      }
      // The server's file name says which: a cohort read in aggregate leaves as counts (PT-144).
      const served = response.headers.get("Content-Disposition") ?? "";
      const content = served.includes("mage-x-effectifs") ? "effectifs" : "episodes";
      const url = URL.createObjectURL(await response.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cohort.name.replace(/[^a-zA-Z0-9-_]/g, "_")}_${content}.csv`;
      a.click();
      // After the click has been dispatched: revoking synchronously races the download.
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      console.error(e);
      alert(t`Erreur lors du téléchargement.`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleConsult = (cohort: Cohort) => {
    setConsultCohort(cohort);
  };

  const closeConsult = () => {
    setConsultCohort(null);
  };

  const toggleCompareMode = () => {
    setCompareMode((prev) => {
      const next = !prev;
      if (!next) setSelectedCompareIds([]);
      return next;
    });
  };

  const toggleCompareSelection = (cohort: Cohort) => {
    setSelectedCompareIds((prev) => {
      if (prev.includes(cohort.id)) {
        return prev.filter((id) => id !== cohort.id);
      }
      // A study compares at most MAX_STUDY_COHORTS cohorts: past it, the oldest pick makes room.
      if (prev.length >= MAX_STUDY_COHORTS) {
        return [...prev.slice(1), cohort.id];
      }
      return [...prev, cohort.id];
    });
  };

  const runComparison = () => {
    if (selectedCompareIds.length < 2) return;
    navigate(studyLink(selectedCompareIds, "table"));
  };

  const shelf = useMemo(() => folders ?? [], [folders]);
  const openFolder = openFolderId
    ? (shelf.find((folder) => folder.id === openFolderId) ?? null)
    : null;
  const folderNames = useMemo(
    () => new Map(shelf.map((folder) => [folder.id, folder.name])),
    [shelf],
  );
  // A cohort filed in a classeur that did not load is « Sans classeur » to this page.
  const folderOf = useCallback(
    (cohort: Cohort) =>
      cohort.folder_id && folderNames.has(cohort.folder_id)
        ? cohort.folder_id
        : null,
    [folderNames],
  );
  const searching = catalogFilter.trim() !== "";
  const showsShelf = !openFolder && !searching && shelf.length > 0;

  const filteredCohorts = useMemo(() => {
    // In a classeur: its cohorts. Outside: « Sans classeur », or every classeur when searching.
    let result = cohorts.filter((cohort) =>
      openFolder
        ? folderOf(cohort) === openFolder.id
        : searching || folderOf(cohort) === null,
    );

    if (catalogFilter.trim()) {
      const q = catalogFilter.toLowerCase();
      result = result.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          (d.description && d.description.toLowerCase().includes(q)),
      );
    }

    if (activeFilter === "recent") {
      result.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    } else if (activeFilter === "largest") {
      // "Volumineux" = largest cohorts by patient count.
      result.sort(
        (a, b) =>
          (b.metadata?.patient_count ?? 0) - (a.metadata?.patient_count ?? 0),
      );
    }

    return result;
  }, [cohorts, catalogFilter, activeFilter, openFolder, searching, folderOf]);

  const cohortCount = (folderId: string) =>
    cohorts.filter((cohort) => folderOf(cohort) === folderId).length;

  const handleMoved = (cohortId: string, folderId: string | null) =>
    setCohorts((prev) =>
      prev.map((cohort) =>
        cohort.id === cohortId ? { ...cohort, folder_id: folderId } : cohort,
      ),
    );

  // Files a cohort straight from its card: a drop on a classeur, or on « Sans classeur ».
  const fileFromCard = async (cohort: Cohort, folderId: string | null) => {
    try {
      await fileCohort(cohort.id, folderId);
      handleMoved(cohort.id, folderId);
      const name = cohort.name;
      const folderName = folderId ? folderNames.get(folderId) : undefined;
      toast.success(
        folderName
          ? t`« ${name} » rangé dans « ${folderName} »`
          : t`« ${name} » retiré de son classeur`,
      );
    } catch (err) {
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : t`Erreur lors de l'enregistrement`,
      );
    }
  };

  // Drag and drop: a card onto a classeur tile or the drop bar. UNFILED is « Sans classeur ».
  const drop = useFolderDrop<Cohort>({
    canDrop: (cohort, target) => target !== (folderOf(cohort) ?? UNFILED),
    onDrop: (cohort, target) =>
      void fileFromCard(cohort, target === UNFILED ? null : target),
  });

  // The dialog's item: a new object only when the cohort or its classeur changes, or it resets.
  const movingItem = useMemo(
    () => (moving ? { folder_id: folderOf(moving) } : null),
    [moving, folderOf],
  );

  const keepCreatedFolder = async (name: string) => {
    const folder = await createCohortFolder(name);
    setFolders((prev) => [...(prev ?? []), folder].sort(byName));
    return folder;
  };

  const pageLoading = loading || folders === null;

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-6 pt-10 pb-12 space-y-10">

        {/* PAGE HEADER */}
        <div>
          {openFolder && (
            <button
              type="button"
              onClick={() => showFolder(null)}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary transition-colors mb-4"
            >
              <ArrowLeft className="h-4 w-4" />
              <Trans>Tout le catalogue</Trans>
            </button>
          )}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="h-11 w-11 shrink-0 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md shadow-primary/20">
                {openFolder ? (
                  <FolderClosed className="h-5 w-5 text-white" />
                ) : (
                  <Users className="h-5 w-5 text-white" />
                )}
              </div>
              {openFolder ? (
                <div className="min-w-0">
                  <h1 className="truncate text-2xl font-bold text-slate-900">
                    {openFolder.name}
                  </h1>
                  <p className="text-sm text-slate-500">
                    <Plural
                      value={cohortCount(openFolder.id)}
                      one="# cohorte"
                      other="# cohortes"
                    />
                  </p>
                </div>
              ) : (
                <div>
                  <h1 className="text-2xl font-bold text-slate-900"><Trans>Catalogue</Trans></h1>
                  <p className="text-sm text-slate-500">
                    <Trans>Cohortes de votre spécialité</Trans>
                  </p>
                </div>
              )}
            </div>
            {canFile &&
              (openFolder ? (
                openFolder.can_write && (
                  <FolderActionsMenu
                    triggerVariant="outline"
                    onRename={() => setNaming({ folder: openFolder })}
                    onDelete={() => setDeletingFolder(openFolder)}
                  />
                )
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setNaming({ folder: null })}
                  className="border-slate-200 hover:border-primary/50"
                >
                  <FolderPlus className="mr-2 h-4 w-4" />
                  <Trans>Nouveau classeur</Trans>
                </Button>
              ))}
          </div>
        </div>

        {/* COHORT GRID */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                placeholder={
                  openFolder
                    ? t`Rechercher dans ce classeur...`
                    : t`Filtrer par nom ou description...`
                }
                value={catalogFilter}
                onChange={(e) => setCatalogFilter(e.target.value)}
                className="pl-9"
              />
              {catalogFilter && (
                <button
                  onClick={() => setCatalogFilter("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {(["all", "recent", "largest"] as FilterType[]).map((key) => (
                <Badge
                  key={key}
                  variant={activeFilter === key ? "default" : "outline"}
                  className={`cursor-pointer px-3 py-1 text-sm transition-all ${
                    activeFilter === key
                      ? "bg-primary text-white hover:bg-primary/90"
                      : "hover:bg-primary/5 hover:border-primary/50 text-slate-600"
                  }`}
                  onClick={() => setActiveFilter(key)}
                >
                  {key === "all" ? t`Toutes` : key === "recent" ? t`Récentes` : t`Volumineuses`}
                </Badge>
              ))}
              {!pageLoading && (
                <span className="text-sm text-slate-400">
                  {filteredCohorts.length}
                </span>
              )}
              <Button
                data-tour="catalogue-compare"
                variant={compareMode ? "default" : "outline"}
                size="sm"
                className={
                  compareMode
                    ? "bg-primary hover:bg-primary/90"
                    : "hover:border-primary/50 hover:text-primary"
                }
                onClick={toggleCompareMode}
              >
                <GitCompareArrows className="h-4 w-4 mr-1.5" />
                <Trans>Comparer</Trans>
              </Button>
            </div>
          </div>

          {!pageLoading && !error && showsShelf && (
            <section className="pt-2">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                <Trans>Classeurs</Trans>
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {shelf.map((folder) => {
                  const writable = canFile && folder.can_write;
                  return (
                    <FolderTile
                      key={folder.id}
                      folder={folder}
                      count={
                        <Plural
                          value={cohortCount(folder.id)}
                          one="# cohorte"
                          other="# cohortes"
                        />
                      }
                      drop={drop}
                      onOpen={() => showFolder(folder.id)}
                      onRename={
                        writable ? () => setNaming({ folder }) : undefined
                      }
                      onDelete={
                        writable ? () => setDeletingFolder(folder) : undefined
                      }
                    />
                  );
                })}
              </div>
              <h2 className="mt-8 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                <Inbox className="h-4 w-4" />
                <Trans>Sans classeur</Trans>
              </h2>
            </section>
          )}

          {pageLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-6 bg-slate-200 rounded w-3/4" />
                  </CardHeader>
                  <CardContent>
                    <div className="h-4 bg-slate-100 rounded w-full mb-2" />
                    <div className="h-4 bg-slate-100 rounded w-2/3 mb-4" />
                    <div className="flex gap-2">
                      <div className="h-8 bg-slate-100 rounded w-20" />
                      <div className="h-8 bg-slate-100 rounded w-20" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Users className="h-12 w-12 text-slate-300 mb-4" />
              <p className="text-red-500 text-lg">{error}</p>
              <Button variant="outline" className="mt-4" onClick={loadCohorts}>
                <Trans>Réessayer</Trans>
              </Button>
            </div>
          ) : openFolderId && !openFolder ? (
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
              <h3 className="text-lg font-semibold mb-2">
                <Trans>Classeur introuvable</Trans>
              </h3>
              <Button variant="outline" onClick={() => showFolder(null)}>
                <Trans>Tout le catalogue</Trans>
              </Button>
            </div>
          ) : filteredCohorts.length === 0 && openFolder && !searching ? (
            <p className="rounded-lg border-2 border-dashed py-8 text-center text-sm text-slate-500">
              <Trans>
                Ce classeur est vide. Glissez-y une cohorte depuis le catalogue,
                ou rangez-la depuis sa carte.
              </Trans>
            </p>
          ) : filteredCohorts.length === 0 && showsShelf ? (
            <p className="rounded-lg border-2 border-dashed py-8 text-center text-sm text-slate-500">
              <Trans>Toutes les cohortes sont rangées dans un classeur.</Trans>
            </p>
          ) : filteredCohorts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Users className="h-16 w-16 text-slate-200 mb-4" />
              {catalogFilter ? (
                <>
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">
                    <Trans>Aucun résultat</Trans>
                  </h3>
                  <p className="text-slate-500 mb-4">
                    <Trans>Aucune cohorte ne correspond à «&nbsp;{catalogFilter}&nbsp;»</Trans>
                  </p>
                  <Button variant="outline" onClick={() => setCatalogFilter("")}>
                    <Trans>Effacer le filtre</Trans>
                  </Button>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">
                    <Trans>Aucune cohorte</Trans>
                  </h3>
                  <p className="text-slate-500 mb-4 max-w-md text-center">
                    <Trans>
                      Les cohortes se créent depuis le tableau de bord : appliquez des filtres puis cliquez sur « Sauvegarder comme cohorte ».
                    </Trans>
                  </p>
                  <Button asChild>
                    <Link to="/dashboard">
                      <BarChart3 className="h-4 w-4 mr-1.5" />
                      <Trans>Aller au dashboard</Trans>
                    </Link>
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCohorts.map((cohort) => {
                const isSelected = selectedCompareIds.includes(cohort.id);
                // In compare mode a click selects the card: it is not dragged then.
                const draggable = canFile && !compareMode;
                const filedIn =
                  searching && !openFolder ? folderOf(cohort) : null;
                return (
                <Card
                  key={cohort.id}
                  className={`group transition-all duration-200 flex flex-col ${
                    compareMode
                      ? `cursor-pointer ${
                          isSelected
                            ? "border-primary ring-2 ring-primary/30 shadow-md"
                            : "hover:shadow-lg hover:border-primary/50"
                        }`
                      : "hover:shadow-lg hover:border-primary/50"
                  }${draggable ? " cursor-grab active:cursor-grabbing" : ""}${
                    drop.dragged?.id === cohort.id ? " opacity-50" : ""
                  }`}
                  draggable={draggable}
                  onDragStart={
                    draggable ? (event) => drop.startDrag(cohort, event) : undefined
                  }
                  onDragEnd={drop.endDrag}
                  onClick={
                    compareMode
                      ? () => toggleCompareSelection(cohort)
                      : undefined
                  }
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg font-semibold text-slate-900 group-hover:text-primary transition-colors">
                          {cohort.name}
                        </CardTitle>
                        <div className="flex items-center gap-1.5 mt-1">
                          <Badge className="bg-blue-100 text-blue-900 hover:bg-blue-100 gap-1 text-xs px-2 py-0.5">
                            <Users className="h-3 w-3" />
                            <Trans>Cohorte</Trans>
                          </Badge>
                          {cohort.metadata?.patient_count != null && (
                            <span className="text-xs text-slate-400">
                              {cohort.metadata.patient_count} <Trans>patients</Trans>
                            </span>
                          )}
                          {cohort.metadata?.episode_count != null && (
                            <span className="text-xs text-slate-400">
                              · {cohort.metadata.episode_count} <Trans>épisodes</Trans>
                            </span>
                          )}
                        </div>
                      </div>
                      {compareMode ? (
                        <div className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleCompareSelection(cohort)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={t`Sélectionner cette cohorte pour comparaison`}
                          />
                        </div>
                      ) : canFile ? (
                        // The datasets' card menu (PT-112), for the same two gestures.
                        <div className="ml-2 flex-shrink-0 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label={t`Actions de la cohorte`}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() => setMoving(cohort)}
                              >
                                <FolderInput className="mr-2 h-4 w-4" />
                                <Trans>Ranger dans un classeur</Trans>
                              </DropdownMenuItem>
                              {folderOf(cohort) && (
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={() => void fileFromCard(cohort, null)}
                                >
                                  <FolderMinus className="mr-2 h-4 w-4" />
                                  <Trans>Retirer du classeur</Trans>
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ) : (
                        <div className="h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0 ml-2">
                          <Users className="h-4 w-4 text-primary" />
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1">
                    <p className="text-slate-500 text-sm line-clamp-2 mb-4 flex-1">
                      {cohort.description || t`Aucune description disponible`}
                    </p>

                    <div className="flex flex-wrap gap-3 text-xs text-slate-400 mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDate(cohort.created_at)}
                      </span>
                      {cohort.metadata?.snapshot_at && (
                        <span
                          className="flex items-center gap-1"
                          title={t`Dernier rafraîchissement`}
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                          {formatDate(cohort.metadata.snapshot_at)}
                        </span>
                      )}
                      {filedIn && (
                        <span className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-blue-50 px-2 py-0.5 text-primary">
                          <FolderClosed className="h-3 w-3 shrink-0" />
                          <span className="truncate">
                            {folderNames.get(filedIn)}
                          </span>
                        </span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        className="flex-1 bg-primary hover:bg-primary/90"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConsult(cohort);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1.5" />
                        <Trans>Consulter</Trans>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="hover:border-primary/50 hover:text-primary"
                        asChild
                        title={t`Étudier cette cohorte (tests statistiques)`}
                      >
                        <Link
                          to={studyLink([cohort.id])}
                          onClick={(e) => e.stopPropagation()}
                          draggable={false}
                        >
                          <Sigma className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="hover:border-primary/50 hover:text-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDownload(cohort);
                        }}
                        disabled={downloadingId === cohort.id}
                        title={t`Télécharger les épisodes (CSV)`}
                      >
                        {downloadingId === cohort.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                      {currentUser?.id === cohort.owner_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="hover:border-primary/50 hover:text-primary text-slate-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleRefreshCohort(cohort);
                          }}
                          disabled={refreshingId === cohort.id}
                          title={t`Rafraîchir (rejouer la requête sur les données actuelles)`}
                        >
                          {refreshingId === cohort.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {currentUser?.id === cohort.owner_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="hover:border-red-400 hover:text-red-600 text-slate-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDeleteCohort(cohort);
                          }}
                          disabled={deletingId === cohort.id}
                          title={t`Supprimer la cohorte`}
                        >
                          {deletingId === cohort.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Sticky compare bar — beside the sidebar, as the layout's column is (home.layout's margin):
          spanning the viewport, its message started under the sidebar and could not be read. */}
      {compareMode && (
        <div className="fixed bottom-0 left-[var(--sidebar-width,16rem)] right-0 z-30 bg-white border-t border-slate-200 shadow-lg transition-all duration-300">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
            <div className="text-sm text-slate-600 flex items-center gap-2">
              {cohorts.length === 0 ? (
                <span className="text-slate-500">
                  <Trans>
                    Aucune cohorte à comparer dans votre catalogue. Créez-en depuis le tableau de bord en appliquant des filtres puis « Sauvegarder comme cohorte ».
                  </Trans>
                </span>
              ) : cohorts.length === 1 ? (
                <span className="text-slate-500">
                  <Trans>
                    Une seule cohorte disponible. Créez-en une seconde depuis le tableau de bord pour activer la comparaison.
                  </Trans>
                </span>
              ) : selectedCompareIds.length === 0 ? (
                <Trans>Sélectionnez de 2 à {MAX_STUDY_COHORTS} cohortes à comparer.</Trans>
              ) : selectedCompareIds.length === 1 ? (
                <Trans>1 cohorte sélectionnée — choisissez-en au moins une autre.</Trans>
              ) : (
                <Trans>{selectedCompareIds.length} cohortes sélectionnées.</Trans>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleCompareMode}
              >
                <X className="h-4 w-4 mr-1.5" />
                <Trans>Annuler</Trans>
              </Button>
              {cohorts.length < 2 ? (
                <Button
                  variant="default"
                  size="sm"
                  className="bg-primary hover:bg-primary/90"
                  asChild
                >
                  <Link to="/dashboard">
                    <BarChart3 className="h-4 w-4 mr-1.5" />
                    <Trans>Aller au dashboard</Trans>
                  </Link>
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  className="bg-primary hover:bg-primary/90"
                  disabled={selectedCompareIds.length < 2}
                  onClick={runComparison}
                >
                  <GitCompareArrows className="h-4 w-4 mr-1.5" />
                  <Trans>Comparer ces cohortes</Trans>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {drop.dragged && (
        <FolderDropBar
          folders={shelf}
          unfiled={UNFILED}
          drop={drop}
          emptyText={<Trans>Aucun classeur : créez-en un d'abord.</Trans>}
        />
      )}

      <FolderNameDialog
        open={naming !== null}
        onOpenChange={(open) => !open && setNaming(null)}
        initialName={naming?.folder?.name ?? ""}
        description={
          <Trans>
            Un classeur regroupe des cohortes du catalogue. Il n'est visible
            que par vous.
          </Trans>
        }
        placeholder={t`Ex. : Arthrodèses lombaires`}
        onSubmit={async (name) => {
          if (naming?.folder) {
            await renameCohortFolder(naming.folder.id, name);
          } else {
            await createCohortFolder(name);
          }
          reloadFolders();
        }}
      />
      <DeleteFolderDialog
        folder={deletingFolder}
        description={
          <Trans>
            Supprimer le classeur « {deletingFolder?.name} » ? Les cohortes
            qu'il contient ne sont pas supprimées : elles retournent dans « Sans
            classeur ».
          </Trans>
        }
        deleteFolder={deleteCohortFolder}
        onClose={() => setDeletingFolder(null)}
        onDeleted={() => {
          const deleted = deletingFolder?.id;
          if (deleted === openFolderId) showFolder(null);
          setCohorts((prev) =>
            prev.map((cohort) =>
              cohort.folder_id === deleted
                ? { ...cohort, folder_id: null }
                : cohort,
            ),
          );
          reloadFolders();
        }}
      />
      <MoveToFolderDialog
        item={movingItem}
        folders={shelf}
        description={
          <Trans>
            « {moving?.name} » sera rangé dans votre catalogue : vos collègues
            ne voient pas vos classeurs.
          </Trans>
        }
        placeholder={t`Ex. : Arthrodèses lombaires`}
        createFolder={keepCreatedFolder}
        fileItem={(folderId) =>
          moving ? fileCohort(moving.id, folderId) : Promise.resolve()
        }
        onClose={() => setMoving(null)}
        onMoved={(folderId) => {
          if (moving) handleMoved(moving.id, folderId);
        }}
      />

      {shareCap.dialog}

      {/* Pop-up Consulter */}
      <Dialog
        open={!!consultCohort}
        onOpenChange={(open) => !open && closeConsult()}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              {consultCohort?.name} — <Trans>Statistiques</Trans>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[calc(90vh-4rem)]">
            {consultCohort && (
              <div className="px-6 pb-6">
                <ScopedPathwayBoard
                  key={consultCohort.id}
                  cohortId={consultCohort.id}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
